// src/modules/ai-agent/AICallLogs.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import Pagination from '../../components/shared/Pagination';
import { getCallLogs, getCallLog, getCallLogStats } from './aiAgentService';
import { FaHistory, FaPhone, FaClock, FaUser, FaArrowRight, FaTimes, FaChartBar } from 'react-icons/fa';

const Container = styled.div`
  padding: 20px;
`;

const PageTitle = styled.h1`
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 16px;
  text-align: center;
`;

const StatValue = styled.div`
  color: ${props => props.theme.colors.primary};
  font-size: 1.6rem;
  font-weight: 700;
`;

const StatLabel = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.8rem;
  margin-top: 4px;
`;

const LogCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props => props.theme.colors.background};
  }
`;

const LogInfo = styled.div`
  flex: 1;
`;

const LogTitle = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LogMeta = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
  margin-top: 4px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const OutcomeBadge = styled.span`
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 500;
  background-color: ${props => {
    switch(props.outcome) {
      case 'completed': return '#4caf5033';
      case 'transferred': return '#2196f333';
      case 'caller_hangup': return '#ff980033';
      case 'error': return '#fa7f7f33';
      case 'timeout': return '#9e9e9e33';
      default: return '#9e9e9e33';
    }
  }};
  color: ${props => {
    switch(props.outcome) {
      case 'completed': return '#4caf50';
      case 'transferred': return '#2196f3';
      case 'caller_hangup': return '#ff9800';
      case 'error': return '#fa7f7f';
      case 'timeout': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  }};
`;

// Detail modal
const DetailOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0,0,0,0.6);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 60px;
  overflow-y: auto;
`;

const DetailPanel = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  padding: 24px;
  margin-bottom: 40px;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const DetailTitle = styled.h2`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 1.1rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px;

  &:hover { color: ${props => props.theme.colors.textPrimary}; }
`;

const ConversationBubble = styled.div`
  padding: 10px 14px;
  border-radius: 12px;
  margin-bottom: 8px;
  max-width: 85%;
  font-size: 0.9rem;
  line-height: 1.4;
  ${props => props.role === 'user' ? `
    background-color: ${props.theme.colors.primary}22;
    color: ${props.theme.colors.textPrimary};
    margin-left: auto;
    border-bottom-right-radius: 4px;
  ` : props.role === 'assistant' ? `
    background-color: ${props.theme.colors.background};
    color: ${props.theme.colors.textPrimary};
    margin-right: auto;
    border-bottom-left-radius: 4px;
  ` : `
    background-color: #2196f322;
    color: #90caf9;
    margin-right: auto;
    font-size: 0.8rem;
    border-bottom-left-radius: 4px;
  `}
`;

const RoleLabel = styled.div`
  font-size: 0.7rem;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 2px;
  text-align: ${props => props.role === 'user' ? 'right' : 'left'};
`;

const ToolsSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid ${props => props.theme.colors.background};
`;

const ToolEntry = styled.div`
  background-color: ${props => props.theme.colors.background};
  padding: 8px 12px;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 0.85rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.colors.textSecondary};
`;

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const AICallLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const logsPerPage = 20;

  useEffect(() => { loadData(); }, [currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [logsData, statsData] = await Promise.all([
        getCallLogs({ page: currentPage, limit: logsPerPage }),
        currentPage === 1 ? getCallLogStats() : Promise.resolve(stats)
      ]);
      setLogs(logsData.logs);
      setTotalItems(logsData.totalItems);
      if (statsData && statsData !== stats) setStats(statsData);
    } catch (err) {
      console.error('Error loading call logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id) => {
    try {
      const detail = await getCallLog(id);
      setSelectedLog(detail);
    } catch (err) {
      console.error('Error loading call detail:', err);
    }
  };

  if (loading && logs.length === 0) {
    return <DashboardLayout><Container><p style={{ color: '#ccc' }}>Loading...</p></Container></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <Container>
        <PageTitle><FaHistory /> AI Call Logs</PageTitle>

        {stats && (
          <StatsRow>
            <StatCard>
              <StatValue>{stats.totalCalls}</StatValue>
              <StatLabel>Total Calls</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.last24h}</StatValue>
              <StatLabel>Last 24h</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{stats.last7d}</StatValue>
              <StatLabel>Last 7 Days</StatLabel>
            </StatCard>
            <StatCard>
              <StatValue>{formatDuration(stats.avgDurationSeconds)}</StatValue>
              <StatLabel>Avg Duration</StatLabel>
            </StatCard>
          </StatsRow>
        )}

        {logs.length === 0 ? (
          <EmptyState>No AI call logs yet. Logs will appear here once callers interact with the AI agent.</EmptyState>
        ) : (
          logs.map(log => (
            <LogCard key={log.id} onClick={() => openDetail(log.id)}>
              <FaPhone style={{ color: '#ccc', flexShrink: 0 }} />
              <LogInfo>
                <LogTitle>
                  {log.caller_number || 'Unknown'}
                  {log.Lead && <span style={{ fontSize: '0.85rem', color: '#ccc' }}><FaArrowRight style={{ margin: '0 4px', fontSize: '0.7rem' }} />{log.Lead.name}</span>}
                </LogTitle>
                <LogMeta>
                  <span><FaClock style={{ marginRight: 4 }} />{formatDate(log.started_at)}</span>
                  <span>{formatDuration(log.duration_seconds)}</span>
                  <OutcomeBadge outcome={log.outcome}>{log.outcome || 'unknown'}</OutcomeBadge>
                </LogMeta>
              </LogInfo>
            </LogCard>
          ))
        )}

        <Pagination
          itemsPerPage={logsPerPage}
          totalItems={totalItems}
          paginate={setCurrentPage}
          currentPage={currentPage}
        />

        {selectedLog && (
          <DetailOverlay onClick={() => setSelectedLog(null)}>
            <DetailPanel onClick={e => e.stopPropagation()}>
              <DetailHeader>
                <DetailTitle>
                  Call from {selectedLog.caller_number || 'Unknown'}
                  {selectedLog.Lead && ` - ${selectedLog.Lead.name}`}
                </DetailTitle>
                <CloseButton onClick={() => setSelectedLog(null)}><FaTimes /></CloseButton>
              </DetailHeader>

              <LogMeta style={{ marginBottom: '16px' }}>
                <span><FaClock style={{ marginRight: 4 }} />{formatDate(selectedLog.started_at)}</span>
                <span>{formatDuration(selectedLog.duration_seconds)}</span>
                <OutcomeBadge outcome={selectedLog.outcome}>{selectedLog.outcome}</OutcomeBadge>
              </LogMeta>

              {/* Conversation */}
              <div>
                {(selectedLog.conversation || []).filter(m => m.role !== 'system').map((msg, i) => (
                  <div key={i}>
                    <RoleLabel role={msg.role}>
                      {msg.role === 'user' ? 'Caller' : msg.role === 'assistant' ? 'AI Agent' : 'Tool Result'}
                    </RoleLabel>
                    <ConversationBubble role={msg.role}>
                      {msg.content || (msg.tool_calls ? `[Called: ${msg.tool_calls.map(t => t.function?.name).join(', ')}]` : '...')}
                    </ConversationBubble>
                  </div>
                ))}
              </div>

              {/* Tools used */}
              {selectedLog.tools_used && selectedLog.tools_used.length > 0 && (
                <ToolsSection>
                  <h4 style={{ color: '#ccc', marginBottom: '8px', fontSize: '0.9rem' }}><FaChartBar style={{ marginRight: 6 }} />Tools Used</h4>
                  {selectedLog.tools_used.map((tool, i) => (
                    <ToolEntry key={i}>
                      <strong>{tool.name}</strong> - {JSON.stringify(tool.args)}
                      {tool.result && <span style={{ color: '#4caf50' }}> {tool.result.success !== false ? 'Success' : 'Failed'}</span>}
                    </ToolEntry>
                  ))}
                </ToolsSection>
              )}
            </DetailPanel>
          </DetailOverlay>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default AICallLogs;
